/**
 * The invite CSV for the onboarding "Invite your team" step (SC-438 D12): columns `email`
 * (required), `first_name`, `last_name`, `role` (optional). A role is a role key: `user` or
 * `manager` as before, `member` or `admin`, or the key of a custom role the person may assign
 * (SC-439). Blank means the role chosen on the step, which is the company default unless they
 * pick another.
 */
import { ROLE_KEY_PATTERN } from '../../../../types/rbac/roles'
import { parseCsv, toCsv } from '../../utils/csv'
import { roleKeyOf } from '../CompanyManagement/utils/roleOptions'

export const INVITE_CSV_COLUMNS = ['email', 'first_name', 'last_name', 'role'] as const
export const INVITE_CSV_DEFAULT_MAX_ROWS = 200
/** The legacy role strings, plus the system role keys, a file may name without being offered them. */
export const INVITE_ROLES = ['user', 'manager', 'member', 'admin'] as const
/** A role value as invites send it: a legacy string or a custom role's key. */
export type InviteRole = string

export interface InviteRow {
  email: string
  firstName: string
  lastName: string
  /** Undefined when the file names none (or the person can't assign roles): the step's role. */
  role?: InviteRole
}

export interface InviteRowNote {
  row: number
  note: string
}

export interface InviteRowError {
  /** The line number in the file, counting the header as row 1, as a spreadsheet shows it. */
  row: number
  reason: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isValidInviteEmail(email: string): boolean {
  return EMAIL_PATTERN.test(email.trim())
}

/** The template people download: the header and one example row. */
export function inviteCsvTemplate(): string {
  return toCsv([[...INVITE_CSV_COLUMNS], ['sam@yourcompany.com', 'Sam', 'Lee', '']])
}

export interface InviteCsvRoleOptions {
  /**
   * Role keys the person may assign (from the roles API, Owner never in it). Left out, the file
   * may name the legacy strings and Member or Admin only, as before roles could be custom.
   */
  assignableKeys?: readonly string[]
  /** The company's default role key; a row asking for it needs no permission. Default `member`. */
  defaultRoleKey?: string
}

/**
 * Checks every row. Good rows are returned to send like typed invites; bad rows come back with
 * their row number and the reason. A file over `maxRows` is refused as a whole, so nobody sends
 * part of a list by accident.
 *
 * Without `canAssignRoles` (no `roles.assign`) a row that asks for a role other than the default
 * is kept but invited with the default, and a note says so, so nobody is skipped and nothing is
 * downgraded silently. With it, a role the person may not assign is a row error.
 */
export function readInviteCsv(
  text: string,
  maxRows: number = INVITE_CSV_DEFAULT_MAX_ROWS,
  canAssignRoles: boolean = true,
  options: InviteCsvRoleOptions = {}
): { rows: InviteRow[]; errors: InviteRowError[]; notes: InviteRowNote[]; fileError?: string } {
  const lines = parseCsv(text)
  if (lines.length === 0) return { rows: [], errors: [], notes: [], fileError: 'The file is empty.' }

  const header = lines[0].map((h) => h.trim().toLowerCase())
  const emailAt = header.indexOf('email')
  if (emailAt < 0) {
    return { rows: [], errors: [], notes: [], fileError: `The first row must name the columns: ${INVITE_CSV_COLUMNS.join(',')}.` }
  }
  const at = (name: string) => header.indexOf(name)
  const body = lines.slice(1)
  if (body.length > maxRows) {
    return { rows: [], errors: [], notes: [], fileError: `The file has ${body.length} people. The limit is ${maxRows} per upload.` }
  }

  const defaultKey = options.defaultRoleKey || 'member'
  const assignable = options.assignableKeys
  const isLegacy = (value: string) => (INVITE_ROLES as readonly string[]).includes(value)
  // A role the person may name: a legacy string or system key that they may assign, or a key from
  // the list. Without a list, only the legacy strings (and Admin, which the cloud will check).
  const mayAssign = (value: string) =>
    assignable ? assignable.includes(roleKeyOf(value)) : isLegacy(value)

  const rows: InviteRow[] = []
  const errors: InviteRowError[] = []
  const notes: InviteRowNote[] = []
  const seen = new Set<string>()
  body.forEach((fields, i) => {
    const rowNumber = i + 2
    const read = (index: number) => (index >= 0 ? (fields[index] || '').trim() : '')
    const email = read(emailAt).toLowerCase()
    const asked = read(at('role')).toLowerCase()
    let role: InviteRole | undefined = asked || undefined

    if (!email) return errors.push({ row: rowNumber, reason: 'No email.' })
    if (!isValidInviteEmail(email)) return errors.push({ row: rowNumber, reason: `"${email}" is not an email address.` })
    if (asked && !isLegacy(asked) && !ROLE_KEY_PATTERN.test(asked)) {
      return errors.push({ row: rowNumber, reason: `"${asked}" is not a role key.` })
    }
    // Owners are made by a transfer, never by an invite.
    if (asked === 'owner') return errors.push({ row: rowNumber, reason: 'Role "owner" cannot be invited.' })
    if (asked && canAssignRoles && !mayAssign(asked)) {
      return errors.push({ row: rowNumber, reason: `You can't assign the role "${asked}".` })
    }
    if (seen.has(email)) return errors.push({ row: rowNumber, reason: `${email} is already in the file.` })
    seen.add(email)
    if (asked && !canAssignRoles) {
      // Their invites carry the default whatever the file says; only say so when it differs.
      if (roleKeyOf(asked) !== defaultKey) {
        notes.push({
          row: rowNumber,
          note: `${email} asked for a role, which needs permission to assign roles. They will be invited with the company's default role.`,
        })
      }
      role = undefined
    }
    rows.push({ email, firstName: read(at('first_name')), lastName: read(at('last_name')), role })
  })
  return { rows, errors, notes }
}
